import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import { api, formatCurrency, formatDate } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { ArrowLeft, Printer } from "lucide-react";
import SkeletonPage, { SkeletonCard, SkeletonTable } from "@/components/KovaSkeleton";

/**
 * Invoice screen has TWO skins:
 *
 *  - Screen  → dark cyber-enterprise (matches the rest of the ERP).
 *  - Print   → light, high-contrast A4 skin driven by @media print in
 *              index.css.  The Tailwind `print:*` variants below flip the
 *              minority of tokens that need per-element control (dark
 *              green totals, dashed separators, etc.).
 *
 * Everything outside `.print-area` (nav, buttons, sidebar…) is hidden by
 * the global print rules — a plain browser Ctrl+P produces a clean invoice.
 */
export default function Invoice() {
  const { id } = useParams();
  const [sale, setSale] = useState(null);
  const [profile, setProfile] = useState(null);

  useEffect(() => {
    api.get(`/sales/${id}`).then((r) => setSale(r.data));
    api.get(`/business-profile`).then((r) => setProfile(r.data));
  }, [id]);

  if (!sale || !profile) return <SkeletonPage title kpi={false} table={false} />;

  const addressLines = [
    profile.address_line1,
    profile.address_line2,
    [profile.city, profile.state, profile.pincode].filter(Boolean).join(", "),
  ].filter(Boolean);
  const contactParts = [
    profile.phone && `Phone: ${profile.phone}`,
    profile.email && profile.email,
    profile.website && profile.website,
  ].filter(Boolean).join(" · ");

  return (
    <div className="p-8 lg:p-10 max-w-4xl mx-auto">
      <div className="flex justify-between items-center mb-6 no-print">
        <Link
          to="/sales"
          className="inline-flex items-center gap-2 text-sm text-[#8A8F98] hover:text-[#F8F9FA]"
          data-testid="back-to-sales"
        >
          <ArrowLeft size={16} /> Back to Sales
        </Link>
        <Button
          onClick={() => window.print()}
          data-testid="print-invoice-btn"
          className="bg-[#39FF14] hover:bg-[#2ECC71] text-[#0B0F0C] font-semibold rounded-[10px] shadow-[0_0_18px_-4px_rgba(57,255,20,0.4)]"
        >
          <Printer size={16} className="mr-2" /> Print / Save PDF
        </Button>
      </div>

      <div
        className="print-area bg-[#121815] border border-[#3A4640] rounded-[12px] shadow-sm p-10
                   print:bg-white print:border-0 print:rounded-none print:p-0 print:shadow-none"
        data-testid="invoice-print-area"
      >
        {/* Header */}
        <div className="flex justify-between items-start pb-8 border-b-2 border-[#3A4640] print:border-black print-hair">
          <div>
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-[10px] bg-[#0B0F0C] border border-[#3A4640] p-1 flex items-center justify-center shrink-0
                              print:bg-white print:border-black print:rounded-none">
                <img
                  src="/logo.jpg"
                  alt={profile.name || "Three Zee Agro Tech"}
                  className="w-full h-full object-contain"
                />
              </div>
              <div>
                <div className="font-heading font-bold text-2xl tracking-tight text-[#F8F9FA] print:text-black">
                  {profile.name || "Three Zee Agro Tech"}
                </div>
                <div className="text-xs text-[#8A8F98] print-muted">
                  {profile.tagline || "Agricultural Machinery Sales & Service"}
                </div>
              </div>
            </div>
            <div className="text-xs text-[#8A8F98] mt-3 leading-relaxed print-muted">
              {addressLines.length > 0 ? (
                addressLines.map((l, i) => <div key={i}>{l}</div>)
              ) : (
                <div className="italic text-[#8A8F98] print:hidden">
                  Add your business address in Settings → Business Profile
                </div>
              )}
              {contactParts && <div className="mt-1">{contactParts}</div>}
              {profile.gstin && <div className="mt-1">GSTIN: {profile.gstin}</div>}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#39FF14] font-heading font-bold print:text-black">
              Invoice
            </div>
            <div className="font-mono font-bold text-lg mt-1 text-[#F8F9FA] print:text-black">
              {sale.invoice_no}
            </div>
            <div className="text-xs text-[#8A8F98] mt-2 print-muted">
              Date: {formatDate(sale.sale_date)}
            </div>
          </div>
        </div>

        {/* Bill To */}
        <div className="grid grid-cols-2 gap-8 py-8 border-b border-[#3A4640] print-hair-lt">
          <div>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading font-semibold mb-2 print-muted">
              Bill To
            </div>
            <div className="font-heading font-bold text-[#F8F9FA] print:text-black">
              {sale.customer?.name}
            </div>
            <div className="text-sm text-[#8A8F98] mt-0.5 print-muted">
              {sale.customer?.village}
            </div>
            <div className="text-sm text-[#8A8F98] font-mono print-muted">
              {sale.customer?.phone}
            </div>
          </div>
          <div className="text-right">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading font-semibold mb-2 print-muted">
              Payment
            </div>
            <div className="font-medium capitalize text-[#F8F9FA] print:text-black">
              {sale.payment_status}
            </div>
            <div className="text-sm text-[#8A8F98] capitalize mt-0.5 print-muted">
              via {sale.payment_method.replace("_", " ")}
            </div>
          </div>
        </div>

        {/* Items */}
        <table className="w-full mt-6">
          <thead className="border-b border-[#3A4640] print-hair-lt">
            <tr className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading print-muted">
              <th className="text-left py-3">Description</th>
              <th className="text-right py-3">Qty</th>
              <th className="text-right py-3">Unit Price</th>
              <th className="text-right py-3">Amount</th>
            </tr>
          </thead>
          <tbody>
            <tr className="border-b border-[#3A4640] print-hair-lt">
              <td className="py-4">
                <div className="font-medium text-[#F8F9FA] print:text-black">
                  {sale.product?.brand} {sale.product?.model}
                </div>
                <div className="text-xs text-[#8A8F98] mt-0.5 print-muted">
                  {sale.product?.name}
                </div>
              </td>
              <td className="py-4 text-right font-mono text-[#F8F9FA] print:text-black">
                {sale.quantity}
              </td>
              <td className="py-4 text-right font-mono text-[#F8F9FA] print:text-black">
                {formatCurrency(sale.unit_price)}
              </td>
              <td className="py-4 text-right font-mono font-semibold text-[#F8F9FA] print:text-black">
                {formatCurrency(sale.unit_price * sale.quantity)}
              </td>
            </tr>
          </tbody>
        </table>

        {/* Totals */}
        <div className="flex justify-end mt-6">
          <div className="w-72 space-y-2 text-sm">
            <div className="flex justify-between text-[#8A8F98] print-muted">
              <span>Subtotal</span>
              <span className="font-mono">{formatCurrency(sale.unit_price * sale.quantity)}</span>
            </div>
            <div className="flex justify-between text-[#8A8F98] print-muted">
              <span>Discount</span>
              <span className="font-mono">− {formatCurrency(sale.discount)}</span>
            </div>
            <div className="flex justify-between pt-3 border-t-2 border-[#3A4640] font-heading font-bold text-lg print-hair">
              <span className="text-[#F8F9FA] print:text-black">Total</span>
              <span className="font-mono text-[#39FF14] print-accent">{formatCurrency(sale.total_amount)}</span>
            </div>
            <div className="flex justify-between text-[#8A8F98] text-xs pt-2 print-muted">
              <span>Amount Paid</span>
              <span className="font-mono">{formatCurrency(sale.amount_paid)}</span>
            </div>
            <div className="flex justify-between text-red-400 text-xs font-semibold print-danger">
              <span>Balance Due</span>
              <span className="font-mono">{formatCurrency(sale.balance_due)}</span>
            </div>
          </div>
        </div>

        {sale.notes && (
          <div className="mt-8 pt-6 border-t border-[#3A4640] print-hair-lt">
            <div className="text-[10px] uppercase tracking-[0.2em] text-[#8A8F98] font-heading font-semibold mb-2 print-muted">
              Notes
            </div>
            <div className="text-sm text-[#F8F9FA] print:text-black">{sale.notes}</div>
          </div>
        )}

        <div className="mt-12 pt-6 border-t border-[#3A4640] flex justify-between text-xs text-[#8A8F98] print-hair-lt print-muted">
          <div>
            {profile.footer_note ||
              "Thank you for choosing Three Zee Agro Tech. Warranty applies as per manufacturer terms."}
          </div>
          <div className="font-heading">{profile.name || "Three Zee Agro Tech"}</div>
        </div>
      </div>
    </div>
  );
}
